import React from 'react';
import type { LscMember, LscMemberInviteSummary } from '../types';
import {
  formatMemberDeactivationReason,
  getMemberDiscordLabel,
  getMemberInGameLabel,
  memberCanCopyClaimLink,
  memberCanReceiveClaimLink,
  memberWasRemovedByCommissioner,
} from '../utils/memberStatus';
import { formatMemberTeamDisplay, getMemberTeamAssignmentState, memberHasTeamConflict } from '../utils/memberTeamAssignment';
import { getMemberVodSyncReadinessLabel } from '../syncReadiness';
import { memberRoleDisplayLabel } from '../leagueRoles';
import { isCurrentMember, isInactiveMember, isMergedInactiveMember } from '../utils/memberFilter';
import { useLeague } from '../hooks/useLeague';
import CfbInlineTeamChip from './CfbInlineTeamChip';
import {
  formatBulkInviteClipboard,
  formatInviteExpiresIn,
  memberClaimCopy,
  memberStreamsCopy,
} from '../memberConnection';

export { formatBulkInviteClipboard };

interface MembersTableProps {
  members: LscMember[];
  ownerUid?: string | null;
  canEdit?: boolean;
  onEdit?: (member: LscMember) => void;
  onRemove?: (member: LscMember) => void;
  onRestore?: (member: LscMember) => void;
  pendingClaimMemberIds?: Set<string>;
  memberInvites?: Record<string, LscMemberInviteSummary | undefined>;
  memberClaims?: Record<string, LscMemberInviteSummary | undefined>;
  onInviteMember?: (member: LscMember) => void;
  onReinviteMember?: (member: LscMember) => void;
  onUnclaimMember?: (member: LscMember) => void;
  onCopyInviteLink?: (member: LscMember) => void;
  onCopyInviteMessage?: (member: LscMember) => void;
  invitingMemberId?: string | null;
  emptyMessage?: string;
  showInactiveHelp?: boolean;
}

export function formatMemberInviteGlance(
  member: LscMember,
  current?: LscMemberInviteSummary,
  lastClaim?: LscMemberInviteSummary
): string | null {
  const claim = lastClaim?.status === 'claimed' ? lastClaim : current?.status === 'claimed' ? current : undefined;
  const who = claim?.claimed_twitch_login || member.twitch_login;
  const twitchId = claim?.claimed_twitch_user_id || member.twitch_user_id;
  const when = claim?.claimed_at;
  const claimedLine =
    who || twitchId
      ? `Claimed by ${who || 'a Twitch account'}${twitchId ? ` (${twitchId})` : ''}${when ? ` on ${when}` : ''}`
      : null;
  const linked = member.status === 'active' && Boolean(member.user_uid || member.twitch_user_id);
  if (linked && claimedLine) return claimedLine;
  if (linked && (member.twitch_login || member.twitch_user_id)) {
    return `Claimed by ${member.twitch_login || 'a Twitch account'}${
      member.twitch_user_id ? ` (${member.twitch_user_id})` : ''
    }`;
  }
  const parts: string[] = [];
  if (current?.status === 'active' && current.valid) {
    const expiry = formatInviteExpiresIn(current.expires_at);
    parts.push(current.expires_at ? `Unclaimed · ${expiry.label}` : 'Unclaimed · link valid');
  } else if (current?.status === 'expired') {
    parts.push('Unclaimed · invite expired');
  } else if (current?.status === 'revoked') {
    parts.push('Unclaimed · invite revoked');
  }
  if (!linked && claimedLine && claim?.unclaimed_at) {
    parts.push(`Last claim ${who || 'a Twitch account'}${twitchId ? ` (${twitchId})` : ''} on ${when}`);
  }
  const text = parts.filter(Boolean).join('. ');
  return text || null;
}

function memberIdentifierLine(member: LscMember): string | null {
  const parts = [getMemberInGameLabel(member), getMemberDiscordLabel(member)].filter(
    (part) => part && part !== '—'
  );
  return parts.length ? parts.join(' · ') : null;
}

function roleBadge(member: LscMember, ownerUid?: string | null): string | null {
  const label = memberRoleDisplayLabel(member, ownerUid);
  return label === 'League Member' ? null : label;
}

const MembersTable: React.FC<MembersTableProps> = ({
  members,
  ownerUid,
  canEdit = false,
  onEdit,
  onRemove,
  onRestore,
  pendingClaimMemberIds,
  memberInvites,
  memberClaims,
  onInviteMember,
  onReinviteMember,
  onUnclaimMember,
  onCopyInviteLink,
  onCopyInviteMessage,
  invitingMemberId,
  emptyMessage = 'No members in this league workspace yet.',
  showInactiveHelp = false,
}) => {
  if (!members.length) {
    return (
      <div className="space-y-2" data-testid="members-table-empty">
        <p className="text-secondary-400 text-sm">{emptyMessage}</p>
        {showInactiveHelp && (
          <p className="text-xs text-secondary-500">
            Inactive members are preserved for audit history and are hidden from active league
            access.
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {showInactiveHelp && (
        <p className="text-xs text-secondary-500" data-testid="inactive-members-help">
          Inactive members are preserved for audit history and are hidden from active league access.
        </p>
      )}
      <div className="space-y-3 md:hidden" data-testid="members-card-list">
        {members.map((member) => (
          <MemberCard
            key={member.id}
            member={member}
            ownerUid={ownerUid}
            canEdit={canEdit}
            onEdit={onEdit}
            onRemove={onRemove}
            onRestore={onRestore}
            pendingClaimMemberIds={pendingClaimMemberIds}
            memberInvite={memberInvites?.[member.id]}
            lastClaim={memberClaims?.[member.id]}
            onInviteMember={onInviteMember}
            onReinviteMember={onReinviteMember}
            onUnclaimMember={onUnclaimMember}
            onCopyInviteLink={onCopyInviteLink}
            onCopyInviteMessage={onCopyInviteMessage}
            inviting={invitingMemberId === member.id}
          />
        ))}
      </div>
      <div className="hidden md:block rounded-lg border border-secondary-700">
        <table className="w-full table-fixed text-sm" data-testid="members-table">
          <thead className="bg-secondary-800 text-secondary-300">
            <tr>
              <th className="w-[24%] px-3 py-3 text-left font-medium">Member</th>
              <th className="w-[14%] px-3 py-3 text-left font-medium">Team</th>
              <th className="w-[18%] px-3 py-3 text-left font-medium">Claimed</th>
              <th className="w-[22%] px-3 py-3 text-left font-medium">Streams</th>
              <th className="w-[24%] px-3 py-3 text-left font-medium">Invite</th>
              {canEdit && <th className="w-[18%] px-3 py-3 text-left font-medium">Actions</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-secondary-700">
            {members.map((member) => (
              <MemberRow
                key={member.id}
                member={member}
                ownerUid={ownerUid}
                canEdit={canEdit}
                onEdit={onEdit}
                onRemove={onRemove}
                onRestore={onRestore}
                pendingClaimMemberIds={pendingClaimMemberIds}
                memberInvite={memberInvites?.[member.id]}
                lastClaim={memberClaims?.[member.id]}
                onInviteMember={onInviteMember}
                onReinviteMember={onReinviteMember}
                onUnclaimMember={onUnclaimMember}
                onCopyInviteLink={onCopyInviteLink}
                onCopyInviteMessage={onCopyInviteMessage}
                inviting={invitingMemberId === member.id}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

function MemberUnclaimAction({
  member,
  onUnclaimMember,
}: {
  member: LscMember;
  onUnclaimMember?: (member: LscMember) => void;
}) {
  if (!onUnclaimMember) return null;
  if (member.role !== 'member' || member.status !== 'active') return null;
  if (!member.user_uid && !member.twitch_user_id) return null;
  return (
    <button
      type="button"
      className="whitespace-nowrap text-amber-300 hover:text-amber-200 text-sm"
      onClick={() => onUnclaimMember(member)}
      data-testid={`unclaim-member-${member.id}`}
    >
      Unclaim
    </button>
  );
}

function MemberInviteActions({
  member,
  memberInvite,
  onReinviteMember,
  inviting,
}: {
  member: LscMember;
  memberInvite?: LscMemberInviteSummary;
  onReinviteMember?: (member: LscMember) => void;
  inviting?: boolean;
}) {
  if (!memberCanCopyClaimLink(member)) return null;
  const reinvite = Boolean(memberInvite && (memberInvite.valid || memberInvite.status === 'active'));
  if (!reinvite || !onReinviteMember) return null;
  return (
    <button
      type="button"
      className="whitespace-nowrap text-primary-400 hover:text-primary-300 text-sm disabled:opacity-50"
      onClick={() => onReinviteMember(member)}
      disabled={inviting}
      data-testid={`reinvite-member-${member.id}`}
    >
      {inviting ? 'Working...' : 'Re-invite'}
    </button>
  );
}

function MemberNameCell({
  member,
  ownerUid,
  pendingClaimMemberIds,
}: {
  member: LscMember;
  ownerUid?: string | null;
  pendingClaimMemberIds?: Set<string>;
}) {
  const inactive = isInactiveMember(member);
  const identifiers = memberIdentifierLine(member);
  const badge = roleBadge(member, ownerUid);
  const deactivationLabel = formatMemberDeactivationReason(member.deactivation_reason);
  return (
    <div className="min-w-0">
      <div className={`truncate font-medium ${inactive ? 'text-secondary-400' : 'text-white'}`}>
        {member.display_name}
        {badge ? (
          <span className="ml-2 text-xs font-normal text-primary-300" data-testid="member-role-badge">
            {badge}
          </span>
        ) : null}
      </div>
      {identifiers ? <div className="truncate text-xs text-secondary-400">{identifiers}</div> : null}
      {deactivationLabel ? (
        <div className="text-xs text-secondary-500" data-testid="member-deactivation-reason">
          {deactivationLabel}
        </div>
      ) : null}
      {isMergedInactiveMember(member) ? (
        <span className="text-xs text-secondary-500" data-testid="member-merged-badge">
          Merged
        </span>
      ) : null}
      {member.status === 'imported_unclaimed' && pendingClaimMemberIds?.has(member.id) ? (
        <span className="text-xs uppercase tracking-wide text-amber-300" data-testid="member-claim-pending-badge">
          Claim pending
        </span>
      ) : null}
    </div>
  );
}

function MemberTeamCell({ member }: { member: LscMember }) {
  const { league } = useLeague();
  const teamLabel = formatMemberTeamDisplay(member);
  return (
    <div className="flex flex-wrap items-center gap-2">
      {league?.game_key === 'cfb' && teamLabel ? (
        <span data-testid="member-team-label">
          <CfbInlineTeamChip label={teamLabel} showLabel />
        </span>
      ) : (
        <span data-testid="member-team-label">{teamLabel}</span>
      )}
      {memberHasTeamConflict(member) ? (
        <span
          className="inline-flex rounded-full border border-amber-700 bg-amber-950/40 px-2 py-0.5 text-xs text-amber-200"
          data-testid="member-team-conflict-badge"
        >
          Team conflict
        </span>
      ) : getMemberTeamAssignmentState(member) === 'custom_external_divergence' ? (
        <span className="text-xs text-secondary-500" data-testid="member-custom-divergence-note">
          Custom team
        </span>
      ) : null}
    </div>
  );
}

function MemberClaimCell({
  member,
  memberInvite,
}: {
  member: LscMember;
  memberInvite?: LscMemberInviteSummary;
}) {
  const copy = memberClaimCopy(member, memberInvite);
  const claimed = copy.startsWith('Claimed');
  return (
    <div data-testid={`member-claimed-${member.id}`} className={claimed ? 'text-emerald-300' : 'text-amber-200'}>
      <span className="font-semibold">{copy}</span>
    </div>
  );
}

function MemberStreamsCell({ member }: { member: LscMember }) {
  const copy = memberStreamsCopy(member);
  const detected = copy.endsWith('detected');
  return (
    <div
      data-testid={`member-streams-${member.id}`}
      className={detected ? 'text-emerald-300' : 'text-secondary-300'}
    >
      {copy}
    </div>
  );
}

function MemberInviteCell({
  member,
  onCopyInviteLink,
  onCopyInviteMessage,
  onInviteMember,
  inviting,
}: {
  member: LscMember;
  memberInvite?: LscMemberInviteSummary;
  onCopyInviteLink?: (member: LscMember) => void;
  onCopyInviteMessage?: (member: LscMember) => void;
  onInviteMember?: (member: LscMember) => void;
  inviting?: boolean;
}) {
  if (!memberCanCopyClaimLink(member)) {
    return <span className="text-secondary-500">—</span>;
  }
  const showTwitchMessage = memberCanReceiveClaimLink(member);
  const copyLink = onCopyInviteLink || onInviteMember;
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
      {copyLink ? (
        <button
          type="button"
          className="whitespace-nowrap text-sm font-medium text-primary-300 hover:text-primary-200 disabled:opacity-50"
          onClick={() => copyLink(member)}
          disabled={inviting}
          data-testid={`copy-member-invite-${member.id}`}
        >
          {inviting ? 'Working...' : 'Copy link'}
        </button>
      ) : null}
      {showTwitchMessage && onCopyInviteMessage ? (
        <button
          type="button"
          className="whitespace-nowrap text-sm text-primary-300 hover:text-primary-200 disabled:opacity-50"
          onClick={() => onCopyInviteMessage(member)}
          disabled={inviting}
          data-testid={`copy-member-message-${member.id}`}
        >
          Copy message
        </button>
      ) : null}
    </div>
  );
}

function MemberActions({
  member,
  canEdit,
  memberInvite,
  onEdit,
  onRemove,
  onRestore,
  onReinviteMember,
  onUnclaimMember,
  inviting,
}: {
  member: LscMember;
  canEdit: boolean;
  memberInvite?: LscMemberInviteSummary;
  onEdit?: (member: LscMember) => void;
  onRemove?: (member: LscMember) => void;
  onRestore?: (member: LscMember) => void;
  onReinviteMember?: (member: LscMember) => void;
  onUnclaimMember?: (member: LscMember) => void;
  inviting?: boolean;
}) {
  if (!canEdit) return null;
  return (
    <div className="flex flex-nowrap items-center gap-3">
      <MemberInviteActions
        member={member}
        memberInvite={memberInvite}
        onReinviteMember={onReinviteMember}
        inviting={inviting}
      />
      <MemberUnclaimAction member={member} onUnclaimMember={onUnclaimMember} />
      <button
        type="button"
        className="whitespace-nowrap text-primary-400 hover:text-primary-300 text-sm"
        onClick={() => onEdit?.(member)}
        data-testid={`edit-member-${member.id}`}
      >
        Edit
      </button>
      {onRestore && memberWasRemovedByCommissioner(member) && (
        <button
          type="button"
          className="whitespace-nowrap text-primary-300 hover:text-primary-200 text-sm"
          onClick={() => onRestore(member)}
          data-testid={`restore-member-${member.id}`}
        >
          Restore
        </button>
      )}
      {onRemove && isCurrentMember(member) && (
        <button
          type="button"
          className="whitespace-nowrap text-red-300 hover:text-red-200 text-sm"
          onClick={() => onRemove(member)}
          data-testid={`remove-member-${member.id}`}
        >
          Remove
        </button>
      )}
    </div>
  );
}

interface MemberRowProps {
  member: LscMember;
  ownerUid?: string | null;
  canEdit: boolean;
  onEdit?: (member: LscMember) => void;
  onRemove?: (member: LscMember) => void;
  onRestore?: (member: LscMember) => void;
  pendingClaimMemberIds?: Set<string>;
  memberInvite?: LscMemberInviteSummary;
  lastClaim?: LscMemberInviteSummary;
  onInviteMember?: (member: LscMember) => void;
  onReinviteMember?: (member: LscMember) => void;
  onUnclaimMember?: (member: LscMember) => void;
  onCopyInviteLink?: (member: LscMember) => void;
  onCopyInviteMessage?: (member: LscMember) => void;
  inviting?: boolean;
}

function MemberRow({
  member,
  ownerUid,
  canEdit,
  onEdit,
  onRemove,
  onRestore,
  pendingClaimMemberIds,
  memberInvite,
  onInviteMember,
  onReinviteMember,
  onUnclaimMember,
  onCopyInviteLink,
  onCopyInviteMessage,
  inviting,
}: MemberRowProps) {
  const inactive = isInactiveMember(member);
  return (
    <tr
      className={inactive ? 'bg-secondary-900/40 text-secondary-500' : 'hover:bg-secondary-800/50'}
      data-testid="members-table-row"
      data-member-status={member.status || 'active'}
    >
      <td className="px-3 py-3">
        <MemberNameCell member={member} ownerUid={ownerUid} pendingClaimMemberIds={pendingClaimMemberIds} />
      </td>
      <td className="px-3 py-3">
        <MemberTeamCell member={member} />
      </td>
      <td className="px-3 py-3">
        <MemberClaimCell member={member} memberInvite={memberInvite} />
      </td>
      <td className="px-3 py-3">
        <MemberStreamsCell member={member} />
      </td>
      <td className="px-3 py-3">
        <MemberInviteCell
          member={member}
          memberInvite={memberInvite}
          onCopyInviteLink={onCopyInviteLink}
          onCopyInviteMessage={onCopyInviteMessage}
          onInviteMember={onInviteMember}
          inviting={inviting}
        />
      </td>
      {canEdit && (
        <td className="px-3 py-3">
          <MemberActions
            member={member}
            canEdit={canEdit}
            memberInvite={memberInvite}
            onEdit={onEdit}
            onRemove={onRemove}
            onRestore={onRestore}
            onReinviteMember={onReinviteMember}
            onUnclaimMember={onUnclaimMember}
            inviting={inviting}
          />
        </td>
      )}
    </tr>
  );
}

function MemberCard(props: MemberRowProps) {
  const { member, canEdit } = props;
  const inactive = isInactiveMember(member);
  const vodSync = getMemberVodSyncReadinessLabel(member);
  return (
    <div
      className={`space-y-2 rounded-lg border p-3 ${
        inactive ? 'border-secondary-800 bg-secondary-900/40 text-secondary-500' : 'border-secondary-700'
      }`}
      data-testid="members-card"
      data-member-status={member.status || 'active'}
    >
      <MemberNameCell
        member={member}
        ownerUid={props.ownerUid}
        pendingClaimMemberIds={props.pendingClaimMemberIds}
      />
      <MemberTeamCell member={member} />
      <MemberClaimCell member={member} memberInvite={props.memberInvite} />
      <MemberStreamsCell member={member} />
      <MemberInviteCell
        member={member}
        memberInvite={props.memberInvite}
        onCopyInviteLink={props.onCopyInviteLink}
        onCopyInviteMessage={props.onCopyInviteMessage}
        onInviteMember={props.onInviteMember}
        inviting={props.inviting}
      />
      <span className="text-xs text-secondary-500" data-testid="member-vod-sync-ready">
        VOD Sync Ready: {vodSync.ready ? 'Yes' : 'No'} ({vodSync.label})
      </span>
      <MemberActions
        member={member}
        canEdit={canEdit}
        memberInvite={props.memberInvite}
        onEdit={props.onEdit}
        onRemove={props.onRemove}
        onRestore={props.onRestore}
        onReinviteMember={props.onReinviteMember}
        onUnclaimMember={props.onUnclaimMember}
        inviting={props.inviting}
      />
    </div>
  );
}

export default MembersTable;
